import { Page } from "@mb-pro-ui/components";
import { usePageSxProps } from "./styles";
import { Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import TitledCard from "../components/templates/basic/TitledCard";
import { useIntl } from "react-intl";
import { Form } from "react-final-form";
import { ImportForm, MessageItem, RotatingSyncIcon } from "../components/ExportImport";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../utils/ApiProvider";
import { ConfigImportResponse, ImportedConfig } from "../utils/types";
import { useMutation } from "react-query";
import B64FileField from "../components/B64FileField";
import { DeviceConfig } from "@helios/toolbox-types";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import WarningIcon from "@mui/icons-material/Warning";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import { APP_NAME } from "../App";

export const ConfigImportPage = () => {
  const [importFile, setImportFile] = useState<string | null>(null);
  const [imported, setImported] = useState<ConfigImportResponse | null>(null);
  const [saved, setSaved] = useState<{ [unitid: string]: "saving" | "saved" | string | undefined }>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<{ unitid: string; warnings: ImportedConfig["warnings"] } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const warningCount = useMemo(() => (imported ? Object.values(imported).reduce((previous, value) => previous + value.warnings.length, 0) : 0), [imported]);

  const { singlePageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const api = useApi();

  const importQuery = useCallback(
    (importFile: string) => {
      setImported(null);
      setSaved({});
      setErrorMessage(null);
      return api<ConfigImportResponse>(`/units/import`, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ importFile }),
      });
    },
    [api]
  );
  const onImportSuccess = useCallback((response: ConfigImportResponse) => {
    setImported(response);
  }, []);
  const onImportFail = useCallback((err: any) => {
    setErrorMessage(err.data?.error ?? null);
  }, []);
  const { mutate: importConfig, isLoading: importLoading } = useMutation({ mutationFn: importQuery, onSuccess: onImportSuccess, onError: onImportFail });

  const saveQuery = useCallback(
    ({ unitid, config }: { unitid: string; config: DeviceConfig }) => {
      setSaved((old) => {
        old[unitid] = "saving";
        return { ...old };
      });

      return api(`/units/${unitid}/config`, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
      })
        .then(() => {
          setSaved((old) => {
            old[unitid] = "saved";
            return { ...old };
          });
        })
        .catch((err) => {
          throw err;
        });
    },
    [api]
  );
  const saveSuccess = useCallback(() => {
    setSaved((old) => {
      const [unitid] = Object.entries(old).find(([_unitid, state]) => state === "saving") ?? [];
      if (unitid) {
        old[unitid] = "saved";
      }
      return { ...old };
    });
  }, []);
  const saveFailed = useCallback((err) => {
    setSaved((old) => {
      const [unitid] = Object.entries(old).find(([_unitid, state]) => state === "saving") ?? [];
      if (unitid) {
        old[unitid] = err.data?.error ?? err.message ?? err;
      }
      return { ...old };
    });
  }, []);
  const { mutate: saveConfig } = useMutation({ mutationFn: saveQuery, onSuccess: saveSuccess, onError: saveFailed });

  const onImportSubmit = useCallback(
    (formData) => {
      importConfig(formData.importFile);
    },
    [importConfig]
  );

  const openInNewTab = useCallback((unitid: string, config: DeviceConfig) => {
    localStorage.setItem(`loaded-config-${unitid}`, JSON.stringify(config));
    window.open(`${window.location.origin}/configuration/${unitid}`, "_blank");
  }, []);

  const startSaveAll = useCallback(() => {
    if (!imported || Object.keys(imported).length === 0) {
      return;
    }
    setSaved(Object.fromEntries(Object.keys(imported).map((unitid) => [unitid, undefined])));
  }, [imported]);

  const removeFromImport = useCallback(
    (unitid: string) => {
      if (!imported) {
        return;
      }
      delete imported[unitid];
      setImported({ ...imported });
    },
    [imported]
  );

  const openDialog = useCallback((unitid: string, warnings: ImportedConfig["warnings"]) => {
    setDialogData({ unitid, warnings });
    setDialogOpen(true);
  }, []);
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogData(null);
  }, []);

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Configuration import" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    if (!imported || Object.values(saved).find((v) => v === "saving")) {
      return;
    }
    const unsaved = Object.keys(saved).find((unitid) => {
      return saved[unitid] === undefined;
    });
    if (unsaved) {
      saveConfig({ unitid: unsaved, config: imported[unsaved].config });
    }
  }, [imported, saveConfig, saved]);

  return (
    <Page sx={singlePageSx}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TitledCard title={formatMessage({ defaultMessage: "Import" })}>
            <Box sx={{ m: 1 }}>
              <Form<ImportForm>
                onSubmit={onImportSubmit}
                initialValues={{ importFile: "" }}
                render={({ handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <B64FileField
                      name="importFile"
                      highlightDirty={false}
                      sx={{ minHeight: 120, height: 1 / 1 }}
                      disabled={importLoading}
                      onChange={setImportFile}
                    />
                    <Button type="submit" sx={{ mt: 1 }} disabled={importLoading || !importFile}>
                      {formatMessage({ defaultMessage: "Import from this file" })}
                    </Button>
                  </form>
                )}
              />
            </Box>
          </TitledCard>
        </Grid>
        {(importLoading || imported || errorMessage) && (
          <Grid item xs={12}>
            <TitledCard title={formatMessage({ defaultMessage: "Devices" })}>
              <Grid container spacing={1} sx={{ p: 1 }}>
                {importLoading && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<RotatingSyncIcon />}>
                      {formatMessage({ defaultMessage: "Import loading..." })}
                    </Alert>
                  </Grid>
                )}
                {errorMessage && (
                  <Grid item xs={12}>
                    <Alert severity="error">{errorMessage}</Alert>
                  </Grid>
                )}
                {imported && (
                  <Grid item xs={12}>
                    <Alert severity={warningCount === 0 ? "info" : "warning"}>
                      {formatMessage(
                        {
                          defaultMessage:
                            "{importedCount, plural, one {# config} other {# configs}} imported. {warningCount, plural, =0 {No warnings} one {# warning} other {# warnings}} detected.",
                        },
                        {
                          importedCount: Object.keys(imported).length,
                          warningCount,
                        }
                      )}
                    </Alert>
                  </Grid>
                )}
                {imported &&
                  Object.entries(imported).map(([unitid, { config, warnings }]) => (
                    <Grid key={`imported-${unitid}`} item xs={12}>
                      <AlertItem
                        unitid={unitid}
                        config={config}
                        warnings={warnings}
                        saved={saved}
                        openDialog={openDialog}
                        removeFromImport={removeFromImport}
                        openInNewTab={openInNewTab}
                        saveConfig={saveConfig}
                      />
                    </Grid>
                  ))}
                {imported && (
                  <Grid item sx={{ display: "flex", justifyContent: "flex-end" }} xs={12}>
                    <Button color="primary" onClick={startSaveAll} startIcon={<SaveOutlinedIcon />}>
                      {formatMessage({ defaultMessage: "Save all configs" })}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </TitledCard>
          </Grid>
        )}
      </Grid>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        sx={{ ".MuiDialogTitle-root": { backgroundColor: (theme) => theme.palette.warning.main } }}
        fullWidth
      >
        <DialogTitle>{dialogData?.unitid}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            {dialogData?.warnings.map((warning, i) => (
              <Grid item key={`dialog-warning-${i}`} xs={12}>
                <MessageItem message={warning} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="warning">
            {formatMessage({ defaultMessage: "Close" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

const AlertItem = ({
  unitid,
  config,
  warnings,
  saved,
  openDialog,
  removeFromImport,
  openInNewTab,
  saveConfig,
}: {
  unitid: string;
  config: DeviceConfig;
  warnings: ImportedConfig["warnings"];
  saved: { [unitid: string]: "saving" | "saved" | string | undefined };
  openDialog: (unitid: string, warnings: ImportedConfig["warnings"]) => void;
  removeFromImport: (unitid: string) => void;
  openInNewTab: (unitid: string, config: DeviceConfig) => void;
  saveConfig: (params: { unitid: string; config: DeviceConfig }) => void;
}) => {
  const { formatMessage } = useIntl();

  let severity: "info" | "success" | "warning" | "error" | undefined = undefined;
  let color: "info" | "success" | "warning" | "error" | undefined = undefined;
  let icon: ReactNode | undefined = undefined;

  if (saved[unitid] === "saving") {
    severity = "info";
    icon = <RotatingSyncIcon />;
  } else if (saved[unitid] === "saved") {
    severity = "success";
    icon = <PublishedWithChangesIcon fontSize="inherit" />;
  } else if (saved[unitid] !== undefined) {
    severity = "error";
  } else if (warnings.length === 0) {
    severity = "success";
    color = "info";
  } else {
    severity = "warning";
  }

  return (
    <Alert
      severity={severity}
      color={color}
      icon={icon}
      sx={{ alignItems: "center" }}
      action={
        <>
          {warnings.length > 0 && (
            <Button color="warning" startIcon={<WarningIcon />} onClick={() => openDialog(unitid, warnings)}>
              {warnings.length}
            </Button>
          )}
          <Button color="inherit" onClick={() => removeFromImport(unitid)} startIcon={<ClearIcon />}>
            {formatMessage({ defaultMessage: "Remove from import" })}
          </Button>
          <Button color="inherit" onClick={() => openInNewTab(unitid, config)} startIcon={<OpenInNewIcon />}>
            {formatMessage({ defaultMessage: "Open in new tab" })}
          </Button>
          <Button color="inherit" onClick={() => saveConfig({ unitid, config })} startIcon={<SaveOutlinedIcon />}>
            {formatMessage({ defaultMessage: "Save" })}
          </Button>
        </>
      }
    >
      <AlertTitle>{unitid}</AlertTitle>
      {saved[unitid] === undefined && <Typography variant="body2">{formatMessage({ defaultMessage: "Loaded" })}</Typography>}
      {saved[unitid] === "saving" && <Typography variant="body2">{formatMessage({ defaultMessage: "Saving" })}</Typography>}
      {saved[unitid] === "saved" && <Typography variant="body2">{formatMessage({ defaultMessage: "Saved", description: "Import message" })}</Typography>}
      {severity === "error" && saved[unitid] !== undefined && <Typography variant="body2">{saved[unitid]}</Typography>}
    </Alert>
  );
};
